import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import ExportPreviewModal from '../components/ExportPreviewModal';
import { hasNavPermission } from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { isoWeek, isoWeekYear } from '../utils/dates';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/topbarButtons.css';
import '../styles/productionSummary.css';
import '../styles/pageHeader.css';

// Presupuesto de Cajas (Producción): the cajas presupuestadas per farm and ISO week, typed into a
// grid with a row per week and a column per farm, saved cell by cell as the user types. Every week of the year can be
// budgeted (the Desde / Hasta filters list them all; by default this week and the next seven).
// Resumen de Producción compares them with the cajas producidas. "Exportar" previews the grid
// and downloads it as Excel, PDF or JPEG.
// Data: GET/PUT box-budgets/ (core/views.py BoxBudgetView).

const COMPANY_KEY = 'farmershub_budget_company';
const SAVE_DELAY = 700; // ms after the last keystroke before a cell is saved
const DEFAULT_WEEKS = 8;
const WEEK_HEAD_COL = 90; // the Semana column
const FARM_COL = 120; // one per farm; the grid scrolls sideways instead of squeezing them

const fmt = (n) => (n == null ? '' : Number(n).toLocaleString('es-EC'));
const sum = (values) => values.reduce((a, v) => a + (v || 0), 0);
const addUp = (cells) => (cells.some((v) => v != null) ? sum(cells) : null);
const cellKey = (farmId, week) => `${farmId}:${week}`;
const weeksInYear = (year) => isoWeek(new Date(year, 11, 28)); // 28 Dec is always in the last ISO week

const GREEN = '2F7D32';
const LIGHT_GREEN = 'C8E6C9';

// The grid as plain values: a row per week (one value per farm, then the week's total) and the
// Total row (each farm's total); `head` is the header row. Shared by the preview, Excel and PDF.
function gridModel(farms, weeks, valueOf) {
  const rows = weeks.map((w) => {
    const cells = farms.map((f) => valueOf(f.id, w));
    return { name: `Sem. ${w}`, cells, total: addUp(cells) };
  });
  const farmTotals = farms.map((f) => addUp(weeks.map((w) => valueOf(f.id, w))));
  rows.push({ name: 'Total', cells: farmTotals, total: addUp(farmTotals), bold: true });
  return { head: ['Semana', ...farms.map((f) => f.name), 'Total'], rows };
}

async function exportExcel(title, fileName, { head: headCells, rows }) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Presupuesto', { views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }] });
  const width = headCells.length;
  const thin = { style: 'thin', color: { argb: 'FF000000' } };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const solid = (hex) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } });

  sheet.mergeCells(1, 1, 1, width);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.fill = solid(GREEN);
  titleCell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { horizontal: 'center' };
  sheet.addRow([]);
  const head = sheet.addRow(headCells);
  head.eachCell((c) => {
    c.fill = solid(LIGHT_GREEN);
    c.border = border;
    c.font = { size: 9 };
    c.alignment = { horizontal: 'center', wrapText: true };
  });
  rows.forEach((r) => {
    const row = sheet.addRow([r.name, ...r.cells.map((v) => (v == null ? '' : v)), r.total == null ? '' : r.total]);
    row.eachCell({ includeEmpty: true }, (c, col) => {
      c.border = border;
      c.font = { bold: !!r.bold || col === width };
      if (col > 1) {
        c.alignment = { horizontal: 'right' };
        c.numFmt = '#,##0';
      }
    });
  });
  sheet.getColumn(1).width = 12;
  for (let i = 2; i <= width; i += 1) sheet.getColumn(i).width = 15;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function exportPDF(title, fileName, { head, rows }) {
  // Many farms go on A3 with a smaller font so every column still fits across the page.
  const columns = head.length - 2;
  const wide = columns > 10;
  const doc = new jsPDF({ orientation: 'landscape', format: wide ? 'a3' : 'a4' });
  autoTable(doc, {
    startY: 14,
    theme: 'grid',
    head: [
      [{ content: title, colSpan: head.length, styles: { fillColor: `#${GREEN}`, textColor: '#ffffff', halign: 'center' } }],
      head,
    ],
    body: rows.map((r) => [r.name, ...r.cells.map(fmt), fmt(r.total)]),
    styles: {
      fontSize: columns > 20 ? 5.5 : wide ? 6.5 : 8, cellPadding: wide ? 0.8 : 1.2, halign: 'right',
      lineColor: [0, 0, 0], lineWidth: 0.1, textColor: [0, 0, 0],
    },
    headStyles: { fillColor: `#${LIGHT_GREEN}`, textColor: [0, 0, 0], fontStyle: 'normal', halign: 'center' },
    columnStyles: { 0: { halign: 'left', cellWidth: wide ? 18 : 24 } },
    didParseCell: (cell) => {
      if (cell.section !== 'body') return;
      if (rows[cell.row.index].bold || cell.column.index === head.length - 1) cell.cell.styles.fontStyle = 'bold';
    },
  });
  doc.save(`${fileName}.pdf`);
}

// This year: this week and the next ones; another year: its first weeks.
function defaultRange(year) {
  const now = new Date();
  const last = weeksInYear(year);
  const from = year === isoWeekYear(now) ? isoWeek(now) : 1;
  return { from, to: Math.min(last, from + DEFAULT_WEEKS - 1) };
}

export default function BudgetPage() {
  const { user } = useAuth();
  const thisYear = isoWeekYear(new Date());
  const [allFarms, setAllFarms] = useState([]);
  const [companyId, setCompanyId] = useState(() => {
    try { return localStorage.getItem(COMPANY_KEY) || ''; } catch { return ''; }
  }); // '' = Todas las empresas
  const [year, setYear] = useState(thisYear);
  const [range, setRange] = useState(() => defaultRange(thisYear));
  const [data, setData] = useState(null);
  const [budget, setBudget] = useState({}); // "farm:week" -> text being typed / saved value
  const [editing, setEditing] = useState(null); // "farm:week" of the focused cell (shown unformatted)
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [saveState, setSaveState] = useState(null); // 'saving' | 'saved' | {error}
  const [preview, setPreview] = useState(false);
  const timers = useRef({}); // "farm:week" -> { id, farmId, week, year, text } waiting to be saved
  const pending = useRef(0);
  const gridRef = useRef(null);

  const companies = useMemo(() => {
    const byId = new Map(allFarms.map((f) => [String(f.company), f.company_name]));
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allFarms]);

  useEffect(() => {
    api.get('farms/').then((r) => setAllFarms(r.data)).catch(() => { /* the grid still loads */ });
  }, []);

  useEffect(() => {
    if (companyId && allFarms.length && !companies.some((c) => c.id === companyId)) setCompanyId('');
  }, [allFarms.length, companies, companyId]);

  useEffect(() => {
    try { localStorage.setItem(COMPANY_KEY, companyId); } catch { /* ignore */ }
    let stale = false; // a newer request replaced this one
    setLoading(true);
    setStatus(null);
    api.get('box-budgets/', {
      params: { year, week_from: range.from, week_to: range.to, ...(companyId ? { company: companyId } : {}) },
    })
      .then((r) => {
        if (stale) return;
        setData(r.data);
        const next = {};
        Object.entries(r.data.budget).forEach(([farmId, weeks]) => {
          Object.entries(weeks).forEach(([w, boxes]) => { next[cellKey(farmId, w)] = String(boxes); });
        });
        setBudget(next);
      })
      .catch((err) => {
        if (stale) return;
        setData(null);
        setStatus(apiErrorText(err, 'No se pudo cargar el presupuesto.'));
      })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [year, range, companyId]);

  const save = useCallback(({ farmId, week, year: cellYear, text }) => {
    const key = cellKey(farmId, week);
    clearTimeout(timers.current[key]?.id);
    delete timers.current[key];
    pending.current += 1;
    setSaveState('saving');
    api.put('box-budgets/', { farm: farmId, year: cellYear, week, boxes: text === '' ? null : Number(text) })
      .then(() => { pending.current -= 1; if (!pending.current) setSaveState('saved'); })
      .catch((err) => {
        pending.current -= 1;
        setSaveState({ error: apiErrorText(err, 'No se pudo guardar.') });
      });
  }, []);

  // Cells still waiting for their timer are saved right away when the filters change or the page
  // closes (each one keeps the year it was typed for).
  useEffect(() => () => Object.values(timers.current).forEach(save), [year, range, companyId, save]);

  const onChange = (farmId, week, raw) => {
    const text = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    const key = cellKey(farmId, week);
    setBudget((b) => ({ ...b, [key]: text }));
    clearTimeout(timers.current[key]?.id);
    const cell = { farmId, week, year, text };
    timers.current[key] = { ...cell, id: setTimeout(() => save(cell), SAVE_DELAY) };
  };

  const onBlur = (farmId, week) => {
    setEditing(null);
    const cell = timers.current[cellKey(farmId, week)];
    if (cell) save(cell);
  };

  // Enter moves down to the next week of the same farm, like a spreadsheet.
  const onKeyDown = (e, weekIndex, farmId) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const next = gridRef.current?.querySelector(`[data-cell="${weekIndex + 1}:${farmId}"]`);
    if (next) next.focus(); else e.currentTarget.blur();
  };

  const farms = data?.farms || [];
  const weeks = data?.weeks || [];
  const valueOf = (farmId, w) => {
    const text = budget[cellKey(farmId, w)];
    return text ? Number(text) : null;
  };
  const farmTotals = farms.map((f) => addUp(weeks.map((w) => valueOf(f.id, w))));

  const companyName = companies.find((c) => c.id === companyId)?.name;
  const exportTitle = data
    ? `Presupuesto de Cajas ${data.year} · Semanas ${data.week_from}–${data.week_to}${companyName ? ` · ${companyName}` : ''}`
    : '';
  const exportFile = data
    ? `Presupuesto_Cajas_${data.year}_S${data.week_from}-${data.week_to}${companyName ? `_${companyName.replace(/\s+/g, '_')}` : ''}`
    : '';
  const openPreview = () => {
    Object.values(timers.current).forEach(save); // the file must include what was just typed
    setPreview(true);
  };

  const lastWeek = weeksInYear(year);
  const weekOptions = Array.from({ length: lastWeek }, (_, i) => i + 1);
  const yearOptions = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];
  const pickRange = (from, to) => setRange(from <= to ? { from, to } : { from: to, to: from });

  return (
    <div className="companies-root">
      <div className="page-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <div className="ps-topbar-actions">
          {hasNavPermission(user, 'production_summary') && (
            <Link to="/production-summary" className="topbar-btn">📊 Resumen de Producción</Link>
          )}
          <button type="button" className="topbar-btn topbar-btn-primary" onClick={openPreview} disabled={!farms.length}>
            📤 Exportar
          </button>
        </div>
      </div>
      <h1 className="companies-title">✏️ Presupuesto de Cajas</h1>
      <p className="ps-subtitle page-header-divided">
        Cajas presupuestadas por finca y semana. Se guardan solas al escribir; Enter baja a la siguiente semana.
      </p>

      <div className="history-filters">
        {companies.length > 1 && (
          <select className="history-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select
          className="history-select" value={year} aria-label="Año"
          onChange={(e) => { const y = Number(e.target.value); setYear(y); setRange(defaultRange(y)); }}
        >
          {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select
          className="history-select" value={range.from} aria-label="Desde semana"
          onChange={(e) => pickRange(Number(e.target.value), Math.max(Number(e.target.value), range.to))}
        >
          {weekOptions.map((w) => <option key={w} value={w}>Desde semana {w}</option>)}
        </select>
        <select
          className="history-select" value={range.to} aria-label="Hasta semana"
          onChange={(e) => pickRange(Math.min(Number(e.target.value), range.from), Number(e.target.value))}
        >
          {weekOptions.map((w) => <option key={w} value={w}>Hasta semana {w}</option>)}
        </select>
        <span className={`ps-save-state${saveState?.error ? ' ps-save-error' : ''}`} role="status">
          {saveState === 'saving' && 'Guardando…'}
          {saveState === 'saved' && '✓ Guardado'}
          {saveState?.error}
        </span>
      </div>

      {status && <div className="companies-status companies-status-error">{status}</div>}

      {loading && !data ? (
        <div className="companies-card"><div className="companies-empty">Cargando…</div></div>
      ) : data && farms.length === 0 ? (
        <div className="companies-card"><div className="companies-empty">No hay fincas para mostrar.</div></div>
      ) : data && (
        <div className="companies-card ps-block" ref={gridRef}>
          <table className="ps-table ps-budget-grid" style={{ minWidth: WEEK_HEAD_COL + (farms.length + 1) * FARM_COL }}>
            <colgroup>
              <col style={{ width: WEEK_HEAD_COL }} />
              {farms.map((f) => <col key={f.id} />)}
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className="ps-title" colSpan={farms.length + 2}>
                  <span className="ps-title-text">CAJAS PRESUPUESTADAS {data.year}</span>
                </th>
              </tr>
              <tr>
                <th className="ps-farm-col ps-week-head">Semana</th>
                {farms.map((f) => <th key={f.id} className="ps-farm-head" title={f.name}>{f.name}</th>)}
                <th className="ps-farm-head">Total</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map((w, i) => (
                <tr key={w}>
                  <td className="ps-farm-col">Sem. {w}</td>
                  {farms.map((f) => {
                    const key = cellKey(f.id, w);
                    return (
                      <td key={f.id} className="ps-budget-cell">
                        <input
                          className="ps-budget-input" inputMode="numeric" data-cell={`${i}:${f.id}`}
                          value={editing === key ? budget[key] ?? '' : fmt(valueOf(f.id, w))}
                          aria-label={`Cajas presupuestadas ${f.name} semana ${w}`}
                          onFocus={(e) => { setEditing(key); e.target.select(); }}
                          onChange={(e) => onChange(f.id, w, e.target.value)}
                          onBlur={() => onBlur(f.id, w)}
                          onKeyDown={(e) => onKeyDown(e, i, f.id)}
                        />
                      </td>
                    );
                  })}
                  <td className="ps-num ps-total-col">{fmt(addUp(farms.map((f) => valueOf(f.id, w))))}</td>
                </tr>
              ))}
              <tr className="ps-total">
                <td className="ps-farm-col">Total</td>
                {farmTotals.map((v, idx) => <td key={farms[idx].id} className="ps-num">{fmt(v)}</td>)}
                <td className="ps-num ps-total-col">{fmt(addUp(farmTotals))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {preview && data && (
        <ExportPreviewModal
          title={exportTitle}
          fileName={exportFile}
          onClose={() => setPreview(false)}
          onDownload={(format) => {
            const model = gridModel(farms, weeks, valueOf);
            return format === 'excel' ? exportExcel(exportTitle, exportFile, model) : exportPDF(exportTitle, exportFile, model);
          }}
        >
          <div className="ps-print">
            <div className="companies-card ps-block">
              {(() => {
                const { head, rows } = gridModel(farms, weeks, valueOf);
                return (
                  <table className="ps-table" style={{ minWidth: WEEK_HEAD_COL + (farms.length + 1) * 90 }}>
                    <colgroup>
                      <col style={{ width: WEEK_HEAD_COL }} />
                      {head.slice(1).map((h) => <col key={h} />)}
                    </colgroup>
                    <thead>
                      <tr><th className="ps-title" colSpan={head.length}>{exportTitle}</th></tr>
                      <tr>
                        <th className="ps-farm-col ps-week-head">{head[0]}</th>
                        {head.slice(1).map((h) => <th key={h} className="ps-farm-head">{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.name} className={r.bold ? 'ps-total' : undefined}>
                          <td className="ps-farm-col">{r.name}</td>
                          {r.cells.map((v, i) => <td key={farms[i].id} className="ps-num ps-num-right">{fmt(v)}</td>)}
                          <td className="ps-num ps-num-right ps-total-col">{fmt(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </ExportPreviewModal>
      )}
    </div>
  );
}
